<script setup>
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import AssistantChat from '~/components/assistant/AssistantChat.vue';

definePageMeta({
  name: 'HowItWorks',
  path: '/help',
});

const {isAuthenticated, canUseAssistant} = useAuthService();

const {setMeta} = useHeadService();
setMeta(
    'Help & support | Tap Leagues',
    'Ask the Tap Leagues assistant anything about using the site (joining clubs, ladders, challenges, results) or get in touch and we’ll help you out.',
);
</script>

<template>
  <base-page>
    <v-container class="large help-wrap">

      <!-- Hero -->
      <div class="tl-card help-hero">
        <div class="help-glow" aria-hidden="true"/>
        <div class="help-hero-inner">
          <div class="help-badge"><v-icon size="30">mdi-robot-happy-outline</v-icon></div>
          <span class="help-eyebrow">Help &amp; support</span>
          <h1 class="help-title">Meet the Tap Leagues assistant</h1>
          <p class="help-sub">
            Our <strong>AI assistant</strong> answers questions about using Tap Leagues (joining clubs,
            ladders and leagues, challenges, entering results) in plain English. It’s in early beta,
            and it lives right here on this page.
          </p>
          <p v-if="canUseAssistant" class="help-sub">Ask it anything below. Prefer a human? We’re always happy to help.</p>
          <p v-else-if="isAuthenticated" class="help-sub">The assistant is in early beta with club admins right now. In the meantime, our team is always happy to help.</p>
          <p v-else class="help-sub">Sign in to get help. Prefer a human? We’re always happy to help.</p>
          <div class="help-cta">
            <v-btn
                v-if="!isAuthenticated"
                color="primary"
                size="large"
                rounded="pill"
                class="px-7"
                :to="{ name: 'Login', params: { authType: 'login' } }"
                prepend-icon="mdi-login"
            >
              Sign in
            </v-btn>
            <v-btn size="large" rounded="pill" class="px-7 help-ghost" :to="{ name: 'Contact' }" prepend-icon="mdi-message-text-outline">
              Contact us
            </v-btn>
            <v-btn size="large" rounded="pill" class="px-7 help-ghost" href="mailto:enquiries@tapleagues.com" prepend-icon="mdi-email-outline">
              enquiries@tapleagues.com
            </v-btn>
          </div>
        </div>
      </div>

      <!-- The assistant (signed-in users) -->
      <assistant-chat v-if="canUseAssistant" class="mt-4"/>

      <!-- Supporting trio -->
      <div class="help-points">
        <div class="tl-card help-point">
          <div class="help-point-ic"><v-icon size="22">mdi-robot-outline</v-icon></div>
          <h3>In beta</h3>
          <p>The assistant helps with using Tap Leagues, but it can make mistakes. The thumbs on each reply help us improve it.</p>
        </div>
        <div class="tl-card help-point">
          <div class="help-point-ic"><v-icon size="22">mdi-rocket-launch-outline</v-icon></div>
          <h3>Always improving</h3>
          <p>We ship updates constantly, and the assistant’s knowledge is refreshed as the product changes.</p>
        </div>
        <div class="tl-card help-point">
          <div class="help-point-ic"><v-icon size="22">mdi-lifebuoy</v-icon></div>
          <h3>Real people, real help</h3>
          <p>Stuck on something the assistant can’t answer? Send us a message and a human will get you sorted.</p>
        </div>
      </div>

    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
.help-wrap {
  max-width: 920px;
}

/* ── Hero ── */
.help-hero {
  position: relative;
  overflow: hidden;
  padding: 36px 28px;

  @media (min-width: 600px) {
    padding: 48px 44px;
  }
}

.help-glow {
  position: absolute;
  top: -120px;
  right: -100px;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(211, 41, 70, 0.12), transparent 68%);
  pointer-events: none;
}

.help-hero-inner {
  position: relative;
  z-index: 1;
  max-width: 640px;
}

.help-badge {
  width: 60px;
  height: 60px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  margin-bottom: 18px;
}

.help-eyebrow {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  color: rgb(var(--v-theme-primary));
}

.help-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
  margin-top: 8px;

  @media (min-width: 600px) {
    font-size: 34px;
  }
}

.help-sub {
  font-size: 14.5px;
  line-height: 1.7;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 14px;

  strong {
    color: rgb(var(--v-theme-text));
    font-weight: 700;
  }
}

.help-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 26px;
}

.help-ghost {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-text));
  border: 1px solid rgb(var(--v-theme-borderLight));
}

/* ── Supporting trio ── */
.help-points {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-top: 18px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.help-point {
  padding: 20px;

  h3 {
    font-size: 14.5px;
    font-weight: 800;
    letter-spacing: -0.2px;
    margin-top: 12px;
  }

  p {
    font-size: 13px;
    line-height: 1.6;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

.help-point-ic {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
}
</style>
